/**
 * Coalescing buffer for board rows.
 *
 * At the busy end of a morning the socket delivers several row updates a
 * second. Humans cannot read faster than about five changes a second and React
 * should not render faster, so patches accumulate here and land as ONE
 * `setQueryData` per flush — which React then commits in one pass.
 *
 * Every server event carries the WHOLE new row, so this is an upsert keyed by
 * revision rather than a merge of partial changes: a stale patch loses on `rev`
 * and is dropped, which is what makes an out-of-order broadcast harmless.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { BoardRow } from '@shared/types';
import { keys } from './keys';

/** ~4.5 flushes a second. Fast enough to feel live, slow enough to read. */
export const FLUSH_MS = 220;

const pending = new Map<string, BoardRow>();
let timer: ReturnType<typeof setTimeout> | null = null;

export function queueBoardRow(client: QueryClient, row: BoardRow): void {
  const held = pending.get(row.conversationId);
  if (held === undefined || row.rev >= held.rev) pending.set(row.conversationId, row);

  timer ??= setTimeout(() => {
    timer = null;
    flushBoard(client);
  }, FLUSH_MS);
}

/**
 * Apply everything held. Exported so a mutation that has just changed a row can
 * make the board agree with the screen it is about to navigate away from,
 * without waiting out the flush interval.
 */
export function flushBoard(client: QueryClient): void {
  if (pending.size === 0) return;
  const batch = new Map(pending);
  pending.clear();

  client.setQueryData<BoardRow[]>(keys.board, (old) => {
    if (old === undefined) return old;

    let changed = false;
    const next: BoardRow[] = [];

    for (const row of old) {
      const patch = batch.get(row.conversationId);
      if (patch === undefined) {
        next.push(row);
        continue;
      }
      batch.delete(row.conversationId);
      // A resolved conversation is off the board: the list is what a human
      // might still have to act on, and the server's own query agrees.
      if (patch.handlingState === 'resolved') {
        changed = true;
        continue;
      }
      if (patch.rev > row.rev) {
        next.push(patch);
        changed = true;
      } else {
        // Untouched rows keep referential identity, so `memo(BoardRow)` skips
        // them and a busy board re-renders only the cells that moved.
        next.push(row);
      }
    }

    // Whatever is left is a conversation the board has never seen — a first
    // message from a new candidate. It belongs on the board immediately.
    for (const row of batch.values()) {
      if (row.handlingState === 'resolved') continue;
      next.push(row);
      changed = true;
    }

    return changed ? next : old;
  });
}

/** Test seam: drop anything buffered and cancel the pending flush. */
export function resetBoardBuffer(): void {
  pending.clear();
  if (timer !== null) clearTimeout(timer);
  timer = null;
}
